import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConversationService, QuizContent, QuizData } from '../../../conversation.service';
import { CheckboxComponent } from 'src/app/shared/components/ui/checkbox/checkbox.component';

@Component({
  selector: 'app-quiz',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatRadioModule, MatIconModule, MatProgressSpinnerModule, FormsModule, TranslateModule, CheckboxComponent],
  templateUrl: './quiz.component.html',
  styleUrls: ['./quiz.component.scss'],
})
export class QuizComponent implements OnInit {
  @Input({ required: true }) data!: QuizContent;
  @Input() isChunkMode = false;
  @Input() contentID: string | undefined;
  @Output() insert = new EventEmitter<any>();

  userAnswers: any[] = [];
  submitted = false;
  score = 0;
  percentage = 0;
  isEvaluating = false;
  evaluationResults: any[] = [];

  constructor(
    private translate: TranslateService,
    private conversationService: ConversationService,
    private cdr: ChangeDetectorRef
  ) {}

  isCollapsed = true;

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }

  isMultiChoice(question: any): boolean {
    return question.type === 'multiple-choice';
  }

  getCorrectIndices(question: any): number[] {
    if (question.correctAnswerIndices && Array.isArray(question.correctAnswerIndices)) {
      return question.correctAnswerIndices;
    }
    if (question.correctAnswerIndex !== undefined && question.correctAnswerIndex !== null && question.correctAnswerIndex !== -1) {
      return [question.correctAnswerIndex];
    }
    return [];
  }

  isOptionCorrect(question: any, optionIndex: number): boolean {
    return this.getCorrectIndices(question).includes(optionIndex);
  }

  toggleOption(questionIndex: number, optionIndex: number) {
    if (this.submitted) return;
    const question = this.currentQuizData.questions[questionIndex];
    if (this.isMultiChoice(question)) {
      const currentAnswers = (this.userAnswers[questionIndex] || []) as number[];
      const idx = currentAnswers.indexOf(optionIndex);
      if (idx > -1) {
        currentAnswers.splice(idx, 1);
      } else {
        currentAnswers.push(optionIndex);
      }
      this.userAnswers[questionIndex] = [...currentAnswers];
    } else {
      this.userAnswers[questionIndex] = optionIndex;
    }
    this.cdr.markForCheck();
  }

  get isAllAnswered(): boolean {
    return !this.userAnswers.some((ans, idx) => {
      const question = this.currentQuizData?.questions[idx];
      if (question?.type === 'free-text') {
        return ans === '';
      }
      if (this.isMultiChoice(question)) {
        return !Array.isArray(ans) || ans.length === 0;
      }
      return ans === -1;
    });
  }

  get hasFreeTextQuestions(): boolean {
    const questions = this.currentQuizData?.questions || [];
    return questions.some((q) => q.type === 'free-text');
  }

  get currentQuizData(): QuizData {
    const lang = this.translate.currentLang || 'en';
    const data = this.data as any;

    // Handle legacy format (flat structure)
    if (data.title && Array.isArray(data.questions)) {
      return data;
    }

    // Handle bilingual format
    return lang === 'fr' ? this.data.fr || this.data.en : this.data.en || this.data.fr;
  }

  ngOnInit() {
    this.reset();
    // In chunk mode, default to collapsed so the quiz doesn't dominate the page
    if (this.isChunkMode) {
      this.isCollapsed = true;
    }
  }

  reset() {
    const questions = this.currentQuizData?.questions || [];
    this.userAnswers = questions.map((q) => {
      if (q.type === 'free-text') return '';
      if (this.isMultiChoice(q)) {
        return [];
      }
      return -1;
    });
    this.submitted = false;
    this.score = 0;
    this.percentage = 0;
    this.evaluationResults = [];
    this.isEvaluating = false;
  }

  submit() {
    if (this.isEvaluating || this.submitted) return;
    const questions = this.currentQuizData.questions;
    if (this.hasFreeTextQuestions) {
      this.isEvaluating = true;
      this.conversationService.evaluateQuiz(questions, this.userAnswers).subscribe({
        next: (res) => {
          this.score = res.score;
          this.percentage = res.percentage;
          this.evaluationResults = res.results || [];
          this.submitted = true;
          this.isEvaluating = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Quiz evaluation failed', err);
          this.isEvaluating = false;
          this.cdr.markForCheck();
        },
      });
    } else {
      this.submitted = true;
      this.score = 0;
      this.userAnswers.forEach((answer, index) => {
        const question = questions[index];
        if (this.isMultiChoice(question)) {
          const correct = this.getCorrectIndices(question);
          const student = answer as number[];
          if (
            correct.length === student.length &&
            correct.every((val) => student.includes(val))
          ) {
            this.score++;
          }
        } else {
          if (answer === question.correctAnswerIndex) {
            this.score++;
          }
        }
      });
      this.percentage = Math.round((this.score / questions.length) * 100);
    }
  }

  isCorrect(index: number): boolean {
    const question = this.currentQuizData.questions[index];
    if (question.type === 'free-text') {
      const evalResult = this.evaluationResults.find((r) => r.questionIndex === index);
      return evalResult ? evalResult.isCorrect : false;
    }
    if (this.isMultiChoice(question)) {
      const correct = this.getCorrectIndices(question);
      const student = this.userAnswers[index] as number[];
      return (
        correct.length === student.length &&
        correct.every((val) => student.includes(val))
      );
    }
    return this.userAnswers[index] === question.correctAnswerIndex;
  }

  getFeedback(index: number): string {
    const evalResult = this.evaluationResults.find((r) => r.questionIndex === index);
    return evalResult ? evalResult.feedback : '';
  }

  isSelected(questionIndex: number, optionIndex: number): boolean {
    const question = this.currentQuizData.questions[questionIndex];
    const ans = this.userAnswers[questionIndex];
    if (this.isMultiChoice(question)) {
      return Array.isArray(ans) && ans.includes(optionIndex);
    }
    return ans === optionIndex;
  }

  getOptionClass(questionIndex: number, optionIndex: number): string {
    const isSelected = this.isSelected(questionIndex, optionIndex);
    if (!this.submitted) {
      return isSelected ? 'selected-option' : '';
    }

    const question = this.currentQuizData.questions[questionIndex];
    const isCorrectOption = this.isOptionCorrect(question, optionIndex);

    if (isCorrectOption) return 'correct-option';
    if (isSelected && !isCorrectOption) return 'incorrect-option';
    return '';
  }

  getExplanationLabel(question: any): string {
    const isFreeText = question.type === 'free-text';
    const key = isFreeText ? 'Quiz.IdealAnswer' : 'Global.Explanation';
    const fallback = isFreeText ? 'Ideal Answer' : 'Explanation';
    const translated = this.translate.instant(key);
    return translated !== key ? translated : fallback;
  }

  handleInsert() {
    if (this.isChunkMode) {
      // In chunk mode, we likely don't need this button, but if we do,
      // it could duplicate the chunk? For now, let's keep the markdown behavior
      // or disable it.
      // But the requirement is: "Update the 'Insert' logic... emit an object".
      // This button is in the Chat Pane (where isChunkMode=false).
      // So if isChunkMode is false (default), we want to EMIT THE OBJECT for the PARENT to create a chunk.
      // Wait, normally `Insert` inserts TEXT into the editor.
      // Now we want `Insert` to CREATE A CHUNK.
      this.insert.emit({ type: 'quiz', content: this.data }); // Emit full bilingual data!
    } else {
      this.insert.emit({ type: 'quiz', content: this.data });
    }
  }
}
